import { Fragment, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { getRegisteredHotkeys } from "@btnet/core-components/Hotkey/hotkey.registry";

import {
    getHotkeyKeys,
    getHotkeyRoot,
    runHotkey,
    type THotkeyItem,
    transposeKey,
    useHotkey,
} from "@buildertrend/components";

import { type ITrackingProp, useTracking } from "utilities/analytics/analytics";

import "./HotkeyDisplay.less";

interface IHotkeyDisplayProps extends ITrackingProp {
    message?: string;
}

/**
 * A hotkey to list in the overlay, from either registry. `key` is always OS-transposed
 * (what BDS stores); `el` is only present for BDS keys bound to an element, which are the
 * ones that also get a positioned tooltip.
 */
interface IOverlayHotkey {
    key: string;
    desc: string;
    el?: THotkeyItem["el"];
}

/**
 * Merges the two hotkey registries the app currently has: the BDS `HotkeyManager` and the
 * BTNet `useHotkey` registry that `<Hotkey>` publishes to. BDS stores keys already
 * transposed for the OS and one entry per key, so BTNet's raw commands ("ctrl+s, cmd+s")
 * get the same treatment before merging. A key in both registries is listed once, keeping
 * the BDS entry because only it can carry the element a tooltip anchors to.
 * TODO (ado-277922): drop the BDS half once all hotkeys are migrated to `<Hotkey>`.
 */
function getOverlayHotkeys(): IOverlayHotkey[] {
    const byKey = new Map<string, IOverlayHotkey>();

    getHotkeyKeys().forEach((item) => {
        byKey.set(item.key, { key: item.key, desc: item.desc, el: item.el });
    });

    getRegisteredHotkeys().forEach(({ command, friendlyName }) => {
        transposeKey(command, "os")
            .split(", ")
            .filter(Boolean)
            .forEach((key) => {
                if (!byKey.has(key)) {
                    byKey.set(key, { key, desc: friendlyName });
                }
            });
    });

    return Array.from(byKey.values());
}

export const HotkeyDisplay = (props: IHotkeyDisplayProps) => {
    const { Track } = useTracking({ component: "HotkeyDisplay" });
    const ref = useRef<HTMLDivElement | null>(null);
    const [visible, setVisible] = useState(false);
    const [transition, setTransition] = useState(false);
    const root = useMemo(() => document.querySelector(getHotkeyRoot()) || document.body, []);

    const open = () => {
        setTransition(true);
        setVisible(true);
    };
    const close = () => {
        setTransition(false);
        const el = ref.current as HTMLDivElement | undefined;
        const afterTransition = () => {
            el?.removeEventListener("animationend", afterTransition);
            setVisible(false);
        };
        el?.addEventListener("animationend", afterTransition);
    };

    const mapKeys = (key: string) => {
        return (
            <span className="hotkey-display--keygroup">
                {transposeKey(key, "user")
                    .split("+")
                    .map((s: string, i: number, list: string[]) => (
                        <Fragment key={`key-${i}`}>
                            <span className="hotkey-display--key">{s}</span>
                            {i < list.length - 1 ? "+" : ""}
                        </Fragment>
                    ))}
            </span>
        );
    };
    const [list, tooltips] = useMemo(() => {
        const rootRect = root.getBoundingClientRect();
        const list: JSX.Element[] = [];
        const tooltips: JSX.Element[] = [];
        getOverlayHotkeys().forEach((item: IOverlayHotkey, index: number) => {
            list.push(
                <li key={index}>
                    <span className="hotkey-display--desc">{item.desc}</span>
                    {mapKeys(item.key)}
                </li>,
            );
            const el = item.el?.current as HTMLElement;
            const elHidden = el && getComputedStyle(el)?.display === "none";
            if (el && el instanceof HTMLElement && !elHidden) {
                const rect = el.getBoundingClientRect();
                const offset = {
                    x: rect.x - rootRect.x + rect.width * 0.5,
                    y: rect.y - rootRect.y + rect.height + 2, // 2 for arrow.
                };
                const pad = 100;
                let className = "";
                if (rootRect.right) {
                    if (offset.x + pad > rootRect.right) {
                        className = "hotkey-display--tooltip-right";
                    } else if (offset.x - pad < rootRect.left) {
                        className = "hotkey-display--tooltip-left";
                    }
                }
                const top = `${offset.y}px`;
                const left = `${offset.x}px`;
                tooltips.push(
                    <li
                        key={item.key}
                        data-testid={`hotkey-display-tooltip-${item.key}`}
                        className={className}
                        style={{ top, left }}
                    >
                        <span className="hotkey-display--desc">{item.desc}</span>
                        <span className="spacer" />
                        {mapKeys(item.key)}
                    </li>,
                );
            }
        });
        return [list, tooltips];
        // Recompute only when the overlay opens/closes: the registries are read imperatively
        // (a fresh array every call), and tooltip positions must be measured against the live
        // DOM at open time.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    let useHotkeyArgs: [
        string,
        string,
        () => void,
        undefined?,
        boolean?,
        boolean?,
        boolean?,
        boolean?,
        boolean?,
    ] = ["ctrl+/", "Show Hotkeys", open, undefined, true, undefined, undefined, undefined, true];
    if (visible) {
        useHotkeyArgs = [
            "ctrl+/",
            "Hide Hotkeys",
            close,
            undefined,
            true,
            true,
            undefined,
            undefined,
            true,
        ];
    }
    useHotkey(...useHotkeyArgs);

    return createPortal(
        <Track>
            <div>
                {visible && (
                    <div
                        className={`hotkey-display ${transition ? "hotkey-display--visible" : ""}`}
                        onClick={() => runHotkey("ctrl+/", true)}
                    >
                        <div ref={ref} className="hotkey-display--legend">
                            <h3>Hotkeys</h3>
                            <div className="hotkey-display--message">{props.message}</div>
                            <ul className="hotkey-display--hotkeys">{list}</ul>
                        </div>
                        <ul className="hotkey-display--tooltips">{tooltips}</ul>
                    </div>
                )}
            </div>
        </Track>,
        root,
    );
};
